import * as Cesium from "cesium";
import type React from "react";

import {
  CAMERA_PIVOT_DURATION_MS,
  HEIGHT_IDEAL_PITCH_TOLERANCE_RAD,
  HEIGHT_VIEW_MAX_RANGE_METERS,
  HEIGHT_VIEW_MIN_RANGE_METERS,
  HEIGHT_VIEW_PITCH_RAD,
} from "./measurementConstants";
import {
  easeOutCubic,
  getViewerCenterGroundPoint,
  isCameraViewLogicalForMeasurement,
  normalizeAngle,
} from "./measurementGeometry";
import type { MeasurementType } from "@/stores/measurementStore";

export const animateCameraPivotInPlaceForMeasurement = (payload: {
  viewer: Cesium.Viewer;
  measurementType: MeasurementType;
  animationFrameRef: React.MutableRefObject<number | null>;
}) => {
  if (payload.animationFrameRef.current !== null) {
    cancelAnimationFrame(payload.animationFrameRef.current);
    payload.animationFrameRef.current = null;
  }

  // Pas de mouvement de caméra pour les mesures de distance au sol.
  if (payload.measurementType === "distance-ground") {
    return;
  }

  const currentPitch = payload.viewer.camera.pitch;
  const targetPitchForMode = HEIGHT_VIEW_PITCH_RAD;
  const tolerance = HEIGHT_IDEAL_PITCH_TOLERANCE_RAD;

  if (
    isCameraViewLogicalForMeasurement({
      viewer: payload.viewer,
      measurementType: payload.measurementType,
      targetPitch: targetPitchForMode,
      pitchTolerance: tolerance,
    })
  ) {
    return;
  }

  const centerPoint = getViewerCenterGroundPoint(payload.viewer);

  if (centerPoint) {
    const currentDistance = Cesium.Cartesian3.distance(
      payload.viewer.camera.positionWC,
      centerPoint,
    );
    const targetRange = Cesium.Math.clamp(
      currentDistance * 0.35,
      HEIGHT_VIEW_MIN_RANGE_METERS,
      HEIGHT_VIEW_MAX_RANGE_METERS,
    );

    payload.viewer.camera.flyToBoundingSphere(
      new Cesium.BoundingSphere(centerPoint, 8),
      {
        duration: 0.55,
        offset: new Cesium.HeadingPitchRange(
          payload.viewer.camera.heading,
          HEIGHT_VIEW_PITCH_RAD,
          targetRange,
        ),
        maximumHeight: targetRange * 2.5,
        easingFunction: Cesium.EasingFunction.QUADRATIC_OUT,
      },
    );
    return;
  }

  const startPosition = Cesium.Cartesian3.clone(payload.viewer.camera.positionWC);
  const startHeading = payload.viewer.camera.heading;
  const startPitch = currentPitch;
  const startRoll = payload.viewer.camera.roll;
  const targetPitch = targetPitchForMode;
  const pitchDelta = normalizeAngle(targetPitch - startPitch);
  const startTime = performance.now();

  const tick = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.max(0, Math.min(1, elapsed / CAMERA_PIVOT_DURATION_MS));
    const eased = easeOutCubic(progress);

    payload.viewer.camera.setView({
      destination: startPosition,
      orientation: {
        heading: startHeading,
        pitch: startPitch + pitchDelta * eased,
        roll: startRoll * (1 - eased),
      },
    });
    payload.viewer.scene.requestRender();

    if (progress < 1) {
      payload.animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      payload.animationFrameRef.current = null;
    }
  };

  payload.animationFrameRef.current = requestAnimationFrame(tick);
};
